import {get, min} from 'es-toolkit/compat'
import {FormikProps, getIn} from 'formik'
import * as Yup from 'yup'

import useKeyPress from '../../../../../commons/src/hooks/use-key-press'
import {PopoverAnchorPlacement} from '../../../../../commons/src/services/position-popover'
import preventDoubleClick from '../../../../../commons/src/services/prevent-double-click'
import {formatCncAxisTier, formatCurrency} from '../../../libs/formatter'
import {PossiblyMixedValue, isMixedValue, unlockMixedValuesLabel} from '../../utils/form'

import {PriceFormula, Provider} from '../../../types'

import closeIcon from '../../../../../commons/src/assets/icons/close.svg'
import plusIcon from '../../../../../commons/src/assets/icons/plus.svg'

import Button from '../../../../../commons/src/components/button'
import FieldLabel from '../../../../../commons/src/components/field-label/FieldLabel'
import FieldNote from '../../../../../commons/src/components/field-note'
import FormRow from '../../../../../commons/src/components/form-row'
import Headline from '../../../../../commons/src/components/headline'
import Icon from '../../../../../commons/src/components/icon'
import InputField from '../../../../../commons/src/components/input-field'
import LabeledCheckbox from '../../../../../commons/src/components/labeled-checkbox-field'
import Link from '../../../../../commons/src/components/link'
import LinkButton from '../../../../../commons/src/components/link-button'
import NumberInputField from '../../../../../commons/src/components/number-input-field'
import Paragraph from '../../../../../commons/src/components/paragraph'
import RadioGroupField from '../../../../../commons/src/components/radio-group-field'
import RadioGroupItem from '../../../../../commons/src/components/radio-group-field/RadioGroupItem'
import Repeatable from '../../../../../commons/src/components/repeatable'
import SelectField from '../../../../../commons/src/components/select-field'
import SelectMenu from '../../../../../commons/src/components/select-menu'
import Stack from '../../../../../commons/src/components/stack'
import Tooltip from '../../../../../commons/src/components/tooltip'
import TooltipHandler from '../../../../../commons/src/components/tooltip-handler'
import MixedValueFormSection from '../../../components/mixed-value-form-section'

export enum PricingType {
  VOLUME = 'volume',
  WEIGHT = 'weight',
}

export enum VolumePricingType {
  SINGLE = 'single',
  STAGGERED = 'staggered',
}

export enum WeightPricingType {
  SINGLE = 'single',
  STAGGERED = 'staggered',
}

export type StaggeredVolumePrice = {volume: string; price: string}
export type VolumePrice = string | StaggeredVolumePrice[]

export type StaggeredWeightPrice = {weight: string; price: string}
export type WeightPrice = string | StaggeredWeightPrice[]
export type CncAxisTier = 3 | 4 | 5 | null

export type SingleFormData = {
  formula: PriceFormula
  name: string
  basePrice: string
  baseUnitPrice: string
  boundingBoxPrice: string
  density: string
  minimumPrice: string
  minimumUnitPrice: string
  quantityDiscount: string
  quantityDiscountCap: string
  surfacePrice: string
  volumePrice: VolumePrice
  weightPrice: WeightPrice
  pricingType: PricingType
  volumePricingType: VolumePricingType
  weightPricingType: WeightPricingType
  useVolumeWithSupport: boolean
  machiningCostPerHour: string
  machiningMaterialRemovalRate: string
  finishingMaterialRemovalRate: string
  cncAxisTier: CncAxisTier
  machineRefixtureTimePerSetup: string
  camProgrammingFeePerSetup: string
  formikGeneral?: unknown // Need type to be able to set general validation error.
}

export type MixedFormData = {
  formula: PossiblyMixedValue<PriceFormula>
  name: PossiblyMixedValue<string>
  basePrice: PossiblyMixedValue<string>
  baseUnitPrice: PossiblyMixedValue<string>
  boundingBoxPrice: PossiblyMixedValue<string>
  density: PossiblyMixedValue<string>
  minimumPrice: PossiblyMixedValue<string>
  minimumUnitPrice: PossiblyMixedValue<string>
  quantityDiscount: PossiblyMixedValue<string>
  quantityDiscountCap: PossiblyMixedValue<string>
  surfacePrice: PossiblyMixedValue<string>
  volumePrice: PossiblyMixedValue<VolumePrice>
  weightPrice: PossiblyMixedValue<WeightPrice>
  pricingType: PossiblyMixedValue<PricingType>
  volumePricingType: PossiblyMixedValue<VolumePricingType>
  weightPricingType: PossiblyMixedValue<WeightPricingType>
  useVolumeWithSupport: PossiblyMixedValue<boolean>
  machiningCostPerHour: PossiblyMixedValue<string>
  machiningMaterialRemovalRate: PossiblyMixedValue<string>
  finishingMaterialRemovalRate: PossiblyMixedValue<string>
  cncAxisTier: PossiblyMixedValue<CncAxisTier>
  machineRefixtureTimePerSetup: PossiblyMixedValue<string>
  camProgrammingFeePerSetup: PossiblyMixedValue<string>
  formikGeneral?: unknown // Need type to be able to set general validation error.
}

const initialValues: Omit<SingleFormData, 'formula'> = {
  name: '',
  basePrice: '',
  baseUnitPrice: '',
  boundingBoxPrice: '',
  minimumPrice: '',
  minimumUnitPrice: '',
  quantityDiscount: '',
  quantityDiscountCap: '',
  surfacePrice: '',
  pricingType: PricingType.VOLUME,
  volumePricingType: VolumePricingType.SINGLE,
  weightPricingType: WeightPricingType.SINGLE,
  volumePrice: '',
  density: '',
  weightPrice: '',
  useVolumeWithSupport: false,
  machiningCostPerHour: '',
  machiningMaterialRemovalRate: '',
  finishingMaterialRemovalRate: '',
  cncAxisTier: null,
  machineRefixtureTimePerSetup: '',
  camProgrammingFeePerSetup: '',
}

export const getInitialValues = (formula: PriceFormula): SingleFormData => ({
  ...initialValues,
  formula,
  pricingType:
    formula === PriceFormula.CNC_MACHINING ? PricingType.WEIGHT : initialValues.pricingType,
})

export const validationSchema = {
  formula: Yup.string().label('Formula').required(),
  name: Yup.string().label('Name').required(),
  basePrice: Yup.number()
    .label('Base price per file')
    .min(0)
    .when(['minimumPrice'], {
      is: (minimumPrice: number) => !!minimumPrice,
      then: schema =>
        schema.lessThan(
          Yup.ref('minimumPrice'),
          'Base price per file must be less than minimum price per file.'
        ),
    }),
  baseUnitPrice: Yup.number()
    .label('Base price per piece')
    .min(0)
    // If there is a baseUnitPrice and we have no mixed value, we need to check if the baseUnitPrice is less than the minimumUnitPrice
    .test(
      'basePrice lower than minimumPrice',
      'Base price per piece must be less than minimum price per piece.',
      (val, ctx) => {
        if (
          val &&
          !isMixedValue(ctx.parent.minimumUnitPrice) &&
          val >= ctx.parent.minimumUnitPrice
        ) {
          return ctx.createError({path: ctx.path})
        }
        return true
      }
    )
    //  If there is a baseUnitPrice and we have a mixed value, we need to check if the baseUnitPrice is less than all of the minimumUnitPrices
    .test(
      'basePrice lower than mixed minimumPrice',
      'Base price per piece must be less than minimum price per piece of all selected pricings.',
      (val, ctx) => {
        if (val && isMixedValue(ctx.parent.minimumUnitPrice)) {
          const minMixedValue: number | undefined = min(ctx.parent.minimumUnitPrice.values)
          if (!!minMixedValue && val >= minMixedValue) {
            return ctx.createError({path: ctx.path})
          }
        }
        return true
      }
    ),
  minimumPrice: Yup.number().label('Minimum price per file').min(0),
  minimumUnitPrice: Yup.number().label('Minimum price per piece').min(0),
  boundingBoxPrice: Yup.number().label('Bounding box price').min(0),
  surfacePrice: Yup.number().label('Surface price').min(0),
  quantityDiscount: Yup.number().label('Quantity discount exponent').max(0),
  quantityDiscountCap: Yup.number()
    .label('Quantity discount exponent Cap')
    .min(0)
    .max(100)
    .test('depends on exponent', 'Exponent value is missing', (val, ctx) => {
      if (val && !ctx.parent.quantityDiscount) {
        return ctx.createError({path: ctx.path})
      }
      return true
    }),
  // options.originalValue is not typed in Yup ResolveOptions
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  volumePrice: Yup.lazy((value: VolumePrice, options: any) => {
    // As we are nesting `Lazy` instances when using `toMixedValidationSchema`, `value` can be undefined
    // at validation time, so we need to rely on the originalValue for determining the VolumePricingType.
    const lazyValue = value || options.originalValue

    if (Array.isArray(lazyValue)) {
      // VolumePricingType.STAGGERED
      return Yup.array()
        .label('Staggered volume price')
        .of(
          Yup.object()
            .shape({
              volume: Yup.number().label('Volume threshold').min(0).required(),
              price: Yup.number().label('Price per cm³').moreThan(0).required(),
            })
            .test('unique', 'Volume threshold must be unique', (val, ctx) => {
              if (
                val &&
                ctx.parent.filter((volumePrice: typeof val) => volumePrice.volume === val.volume)
                  .length > 1
              ) {
                return ctx.createError({path: `${ctx.path}.volume`})
              }
              return true
            })
        )
    }

    // VolumePricingType.SINGLE
    return Yup.number()
      .label('Single volume price')
      .min(0)
      .when(['pricingType', 'volumePricingType'], {
        is: (pricingType: PricingType, volumePricingType: VolumePricingType) =>
          pricingType === PricingType.VOLUME && volumePricingType === VolumePricingType.SINGLE,
        then: schema => schema.required(),
      })
  }),
  // PricingType.WEIGHT
  density: Yup.number()
    .label('Density')
    .moreThan(0)
    .when(['pricingType'], {
      is: (pricingType: PricingType) => pricingType === PricingType.WEIGHT,
      then: schema => schema.required(),
    }),
  // options.originalValue is not typed in Yup ResolveOptions
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  weightPrice: Yup.lazy((value: WeightPrice, options: any) => {
    // As we are nesting `Lazy` instances when using `toMixedValidationSchema`, `value` can be undefined
    // at validation time, so we need to rely on the originalValue for determining the WeightPricingType.
    const lazyValue = value || options.originalValue

    if (Array.isArray(lazyValue)) {
      // WeightPricingType.STAGGERED
      return Yup.array()
        .label('Staggered weight price')
        .of(
          Yup.object()
            .shape({
              weight: Yup.number().label('Weight threshold').min(0).required(),
              price: Yup.number().label('Price per gram').moreThan(0).required(),
            })
            .test('unique', 'Weight threshold must be unique', (val, ctx) => {
              if (
                val &&
                ctx.parent.filter((weightPrice: typeof val) => weightPrice.weight === val.weight)
                  .length > 1
              ) {
                return ctx.createError({path: `${ctx.path}.weight`})
              }
              return true
            })
        )
    }

    // WeightPricingType.SINGLE
    return Yup.number()
      .label('Single weight price')
      .min(0)
      .when(['pricingType', 'weightPricingType'], {
        is: (pricingType: PricingType, weightPricingType: WeightPricingType) =>
          pricingType === PricingType.WEIGHT && weightPricingType === WeightPricingType.SINGLE,
        then: schema => schema.required(),
      })
  }),
  machiningCostPerHour: Yup.number()
    .label('Machining cost per hour')
    .min(0)
    .when(['formula'], {
      is: (val: string) => val === PriceFormula.CNC_MACHINING,
      then: schema => schema.required(),
    }),
  machiningMaterialRemovalRate: Yup.number()
    .label('Machining material removal rate')
    .min(0)
    .when(['formula'], {
      is: (val: string) => val === PriceFormula.CNC_MACHINING,
      then: schema => schema.required(),
    }),
  finishingMaterialRemovalRate: Yup.number()
    .label('Finishing material removal rate')
    .min(0)
    .when(['formula'], {
      is: (val: string) => val === PriceFormula.CNC_MACHINING,
      then: schema => schema.required(),
    }),
  cncAxisTier: Yup.number()
    .label('CNC Axis Tier')
    .nullable()
    .oneOf([3, 4, 5, null], 'CNC Axis Tier must be 3-axis, 4-axis, 5-axis or empty')
    .when(['formula'], {
      is: (val: string) => val === PriceFormula.CNC_MACHINING,
      then: schema => schema.optional(),
    }),
  machineRefixtureTimePerSetup: Yup.number()
    .label('Machine refixture time per setup (in minutes)')
    .min(0)
    .when(['formula'], {
      is: (val: string) => val === PriceFormula.CNC_MACHINING,
      then: schema => schema.optional(),
    }),
  camProgrammingFeePerSetup: Yup.number()
    .label('CAM programming fee per setup')
    .min(0)
    .when(['formula'], {
      is: (val: string) => val === PriceFormula.CNC_MACHINING,
      then: schema => schema.optional(),
    }),
}

type Props = {
  formik: FormikProps<SingleFormData> | FormikProps<MixedFormData>
  provider: Provider
}

const PricingsForm = ({formik, provider}: Props) => {
  useKeyPress(
    preventDoubleClick(() => formik.handleSubmit()),
    'Enter'
  )

  const getInitialStaggeredVolumePrice = (index: number) => ({
    volume: index === 0 ? '0' : '',
    price: '',
  })

  const getInitialStaggeredWeightPrice = (index: number) => ({
    weight: index === 0 ? '0' : '',
    price: '',
  })

  const setPricingType = (
    pricingType: PricingType,
    volumePricingType: VolumePricingType,
    weightPricingType?: WeightPricingType
  ) => {
    let volumePrice: SingleFormData['volumePrice'] = initialValues.volumePrice
    let weightPrice: SingleFormData['weightPrice'] = initialValues.weightPrice

    if (pricingType === PricingType.VOLUME && volumePricingType === VolumePricingType.STAGGERED) {
      volumePrice = [getInitialStaggeredVolumePrice(0), getInitialStaggeredVolumePrice(1)]
    }

    if (pricingType === PricingType.WEIGHT && weightPricingType === WeightPricingType.STAGGERED) {
      weightPrice = [getInitialStaggeredWeightPrice(0), getInitialStaggeredWeightPrice(1)]
    }

    formik.setFieldValue('pricingType', pricingType)
    formik.setFieldValue('volumePricingType', volumePricingType)
    formik.setFieldValue('weightPricingType', weightPricingType || initialValues.weightPricingType)
    // Reset pricing type values when switching the pricing type
    formik.setFieldValue('volumePrice', volumePrice)
    formik.setFieldValue('weightPrice', weightPrice)
    formik.setFieldValue('density', initialValues.density)
  }

  const setPossiblyMixedPricingType = () => {
    let nextPricingType = initialValues.pricingType
    let nextVolumePricingType = initialValues.volumePricingType
    let nextWeightPricingType = initialValues.weightPricingType

    if (!isMixedValue(formik.values.pricingType)) {
      nextPricingType = formik.values.pricingType
    }
    if (!isMixedValue(formik.values.volumePricingType)) {
      nextVolumePricingType = formik.values.volumePricingType
    }
    if (!isMixedValue(formik.values.weightPricingType)) {
      nextWeightPricingType = formik.values.weightPricingType
    }

    setPricingType(nextPricingType, nextVolumePricingType, nextWeightPricingType)
  }

  const volumePricingOptions = [
    {label: 'Single value price', value: VolumePricingType.SINGLE},
    {label: 'Staggered price', value: VolumePricingType.STAGGERED},
  ]

  const weightPricingOptions = [
    {label: 'Single value price', value: WeightPricingType.SINGLE},
    {label: 'Staggered price', value: WeightPricingType.STAGGERED},
  ]

  const cncAxisTierOptions = [
    {label: 'All axis', value: null},
    {label: formatCncAxisTier(3), value: 3},
    {label: formatCncAxisTier(4), value: 4},
    {label: formatCncAxisTier(5), value: 5},
  ]

  const getVolumePricingOption = (optionValue: VolumePricingType) =>
    volumePricingOptions.find(({value}) => value === optionValue)

  const getWeightPricingOption = (optionValue: WeightPricingType) =>
    weightPricingOptions.find(({value}) => value === optionValue)

  const getCncAxisTierOption = (optionValue: CncAxisTier | PossiblyMixedValue<CncAxisTier>) =>
    cncAxisTierOptions.find(({value}) => value === optionValue)

  const hasError = (name: string) => formik.submitCount > 0 && !!getIn(formik.errors, name)

  const renderStaggeredPricing = <T extends {price: string}>(
    values: T[],
    fieldNamePrefix: string,
    thresholdField: keyof T,
    thresholdUnit: string,
    thresholdPlaceholder: string,
    pricePlaceholder: string,
    onAdd: (nextIndex: number) => void,
    onDelete: (index: number) => void
  ) => (
    <FormRow>
      <Repeatable
        preventDeleteMin
        min={2}
        button={<LinkButton icon={<Icon source={plusIcon} />}>Add threshold</LinkButton>}
        onAdd={onAdd}
        onDelete={onDelete}
      >
        {values.map((item, index) => (
          <FormRow key={index} layout="half-half">
            <FieldNote
              appearance="error"
              note={
                formik.submitCount > 0 &&
                get(formik.errors, `${fieldNamePrefix}[${index}].${String(thresholdField)}`)
              }
            >
              <NumberInputField
                allowDecimals
                placeholder={thresholdPlaceholder}
                name={`${fieldNamePrefix}[${index}].${String(thresholdField)}`}
                value={item[thresholdField] as string}
                disabled={index === 0}
                prefix="≥"
                suffix={thresholdUnit}
                appearance={
                  hasError(`${fieldNamePrefix}[${index}].${String(thresholdField)}`)
                    ? 'error'
                    : undefined
                }
                onChange={(value, name) => formik.setFieldValue(name as string, value)}
                onBlur={formik.handleBlur}
              />
            </FieldNote>
            <FieldNote
              appearance="error"
              note={
                formik.submitCount > 0 && get(formik.errors, `${fieldNamePrefix}[${index}].price`)
              }
            >
              <NumberInputField
                allowDecimals
                placeholder={pricePlaceholder}
                name={`${fieldNamePrefix}[${index}].price`}
                value={item.price}
                suffix={formatCurrency(provider.currency)}
                appearance={hasError(`${fieldNamePrefix}[${index}].price`) ? 'error' : undefined}
                onChange={(value, name) => formik.setFieldValue(name as string, value)}
                onBlur={formik.handleBlur}
              />
            </FieldNote>
          </FormRow>
        ))}
      </Repeatable>
    </FormRow>
  )

  const renderDensityField = () => (
    <FieldLabel label="Material density">
      <FieldNote appearance="error" note={formik.submitCount > 0 && formik.errors.density}>
        <NumberInputField
          allowDecimals
          name="density"
          mixedLabel="Mixed"
          mixed={isMixedValue(formik.values.density)}
          value={
            isMixedValue(formik.values.density) ? initialValues.density : formik.values.density
          }
          suffix="g/cm³"
          appearance={hasError('density') ? 'error' : undefined}
          onChange={(value, name) => formik.setFieldValue(name as string, value)}
          onBlur={formik.handleBlur}
          action={
            isMixedValue(formik.values.density) ? renderClearMixedValueButton('density') : undefined
          }
        />
      </FieldNote>
    </FieldLabel>
  )

  const renderClearMixedValueButton = (formikKey: keyof Omit<SingleFormData, 'formula'>) => (
    <Button
      hierarchy="ghost-input-action"
      icon={<Icon block source={closeIcon} title={unlockMixedValuesLabel} />}
      onClick={() => formik.setFieldValue(formikKey, initialValues[formikKey])}
    />
  )

  const renderSlicerUsage = () => (
    <Stack>
      <Headline size="s" label="Use FDM slicer" className="u-no-margin-bottom" />
      <MixedValueFormSection
        locked={isMixedValue(formik.values.useVolumeWithSupport)}
        onClick={() =>
          formik.setFieldValue('useVolumeWithSupport', initialValues.useVolumeWithSupport)
        }
        unlockButtonLabel={unlockMixedValuesLabel}
      >
        <Stack>
          <Paragraph minor size="s" className="u-no-margin-bottom">
            Enable this feature for using the sliced filament volume instead of the geometric model
            volume in your price calculations.
            <br />
            We use{' '}
            <Link href="https://www.prusa3d.com/page/prusaslicer_424/" target="_blank">
              PrusaSlicer 2.7.4
            </Link>
            , with support material calculation enabled, to determine the sliced volume of the
            model.
            <br />
            You can download the slicer profile that the service is using{' '}
            <Link
              href="https://printing-engine-public.s3.eu-west-1.amazonaws.com/PrusaSlicer_Craftcloud_profiles.zip"
              target="_blank"
            >
              here.
            </Link>
            <br />
            Slicing is supported for STL, OBJ, STEP/STP, 3MF and AMF files. For models that fail to
            be sliced, prices will be calculated based on the geometric volume of the model.
            <br />
            The models are sliced for FDM printers. While you are free to use this setting for any
            technology, we do not recommend its use for anything other than FDM/FFF.
          </Paragraph>
          <LabeledCheckbox
            inline
            name="useVolumeWithSupport"
            label="Enable slicer usage"
            checked={
              isMixedValue(formik.values.useVolumeWithSupport)
                ? initialValues.useVolumeWithSupport
                : formik.values.useVolumeWithSupport
            }
            onChange={(value, name) => formik.setFieldValue(name as string, value)}
            onBlur={formik.handleBlur}
          />
        </Stack>
      </MixedValueFormSection>
    </Stack>
  )

  return (
    <form onSubmit={formik.handleSubmit}>
      {formik.errors.formikGeneral && <Paragraph error>{formik.errors.formikGeneral}</Paragraph>}
      <Stack space="300">
        <Stack space="70">
          <Headline size="s" label="Name" className="u-no-margin-bottom" />
          <FormRow>
            <FieldNote appearance="error" note={formik.submitCount > 0 && formik.errors.name}>
              <InputField
                placeholder="for your internal reference only"
                name="name"
                mixedLabel="Mixed"
                mixed={isMixedValue(formik.values.name)}
                value={isMixedValue(formik.values.name) ? initialValues.name : formik.values.name}
                appearance={hasError('name') ? 'error' : undefined}
                onChange={(value, name, event) => formik.handleChange(event)}
                onBlur={formik.handleBlur}
                action={
                  isMixedValue(formik.values.name) ? renderClearMixedValueButton('name') : undefined
                }
              />
            </FieldNote>
          </FormRow>
        </Stack>
        <Stack space="90">
          <Headline
            label={
              formik.values.formula === PriceFormula.CNC_MACHINING
                ? 'Raw material cost'
                : 'Pricing type'
            }
            className="u-no-margin-bottom"
          />
          <MixedValueFormSection
            locked={
              isMixedValue(formik.values.pricingType) ||
              isMixedValue(formik.values.volumePricingType) ||
              isMixedValue(formik.values.volumePrice)
            }
            onClick={setPossiblyMixedPricingType}
            unlockButtonLabel={unlockMixedValuesLabel}
          >
            {formik.values.formula !== PriceFormula.CNC_MACHINING && (
              <>
                <FormRow>
                  <FieldNote
                    appearance="error"
                    note={formik.submitCount > 0 && formik.errors.pricingType}
                  >
                    <RadioGroupField
                      horizontal
                      name="pricingType"
                      value={
                        isMixedValue(formik.values.pricingType)
                          ? initialValues.pricingType
                          : formik.values.pricingType
                      }
                      onChange={value =>
                        setPricingType(
                          value as PricingType,
                          formik.values.volumePricingType as VolumePricingType,
                          formik.values.weightPricingType as WeightPricingType
                        )
                      }
                    >
                      <RadioGroupItem label="Volume price" value={PricingType.VOLUME} />
                      <RadioGroupItem label="Weight price" value={PricingType.WEIGHT} />
                    </RadioGroupField>
                  </FieldNote>
                </FormRow>

                {(formik.values.pricingType === PricingType.VOLUME ||
                  isMixedValue(formik.values.pricingType) ||
                  isMixedValue(formik.values.volumePrice)) && (
                  <>
                    <FormRow layout="half-half">
                      <FieldNote
                        appearance="error"
                        note={formik.submitCount > 0 && formik.errors.volumePricingType}
                      >
                        <SelectField
                          name="volumePricingType"
                          value={
                            isMixedValue(formik.values.pricingType)
                              ? getVolumePricingOption(
                                  initialValues.volumePricingType as VolumePricingType
                                )
                              : getVolumePricingOption(
                                  formik.values.volumePricingType as VolumePricingType
                                )
                          }
                          mixedLabel="Mixed"
                          mixed={isMixedValue(formik.values.pricingType)}
                          menu={<SelectMenu values={volumePricingOptions} />}
                          onChange={value =>
                            setPricingType(
                              formik.values.pricingType as PricingType,
                              value?.value as VolumePricingType,
                              formik.values.weightPricingType as WeightPricingType
                            )
                          }
                          onBlur={formik.handleBlur}
                        />
                      </FieldNote>
                    </FormRow>
                    {(formik.values.volumePricingType === VolumePricingType.SINGLE ||
                      isMixedValue(formik.values.volumePricingType) ||
                      isMixedValue(formik.values.volumePrice)) && (
                      <FormRow layout="half-half">
                        <FieldNote
                          appearance="error"
                          note={formik.submitCount > 0 && formik.errors.volumePrice}
                        >
                          <NumberInputField
                            allowDecimals
                            placeholder="Price per cm³"
                            name="volumePrice"
                            value={
                              isMixedValue(formik.values.volumePrice)
                                ? (initialValues.volumePrice as string)
                                : (formik.values.volumePrice as string)
                            }
                            mixedLabel="Mixed"
                            mixed={isMixedValue(formik.values.volumePrice)}
                            suffix={formatCurrency(provider.currency)}
                            appearance={hasError('volumePrice') ? 'error' : undefined}
                            onChange={(value, name) => formik.setFieldValue(name as string, value)}
                            onBlur={formik.handleBlur}
                          />
                        </FieldNote>
                      </FormRow>
                    )}
                    {!isMixedValue(formik.values.volumePrice) &&
                      formik.values.volumePricingType === VolumePricingType.STAGGERED &&
                      renderStaggeredPricing(
                        formik.values.volumePrice as StaggeredVolumePrice[],
                        'volumePrice',
                        'volume',
                        'cm³',
                        'Volume threshold',
                        'Price per cm³',
                        nextIndex => {
                          formik.setFieldValue('volumePrice', [
                            ...(formik.values.volumePrice as StaggeredVolumePrice[]),
                            getInitialStaggeredVolumePrice(nextIndex),
                          ])
                        },
                        index => {
                          formik.setFieldValue(
                            'volumePrice',
                            (formik.values.volumePrice as StaggeredVolumePrice[]).filter(
                              (_, i) => i !== index
                            )
                          )
                        }
                      )}
                  </>
                )}
              </>
            )}
            {formik.values.pricingType === PricingType.WEIGHT && (
              <>
                <FormRow layout="half-half">
                  <FieldNote
                    appearance="error"
                    note={formik.submitCount > 0 && formik.errors.weightPricingType}
                  >
                    <SelectField
                      name="weightPricingType"
                      value={
                        isMixedValue(formik.values.pricingType)
                          ? getWeightPricingOption(
                              initialValues.weightPricingType as WeightPricingType
                            )
                          : getWeightPricingOption(
                              formik.values.weightPricingType as WeightPricingType
                            )
                      }
                      mixedLabel="Mixed"
                      mixed={isMixedValue(formik.values.pricingType)}
                      menu={<SelectMenu values={weightPricingOptions} />}
                      onChange={value =>
                        setPricingType(
                          formik.values.pricingType as PricingType,
                          formik.values.volumePricingType as VolumePricingType,
                          value?.value as WeightPricingType
                        )
                      }
                      onBlur={formik.handleBlur}
                    />
                  </FieldNote>
                </FormRow>
                {(formik.values.weightPricingType === WeightPricingType.SINGLE ||
                  isMixedValue(formik.values.weightPricingType) ||
                  isMixedValue(formik.values.weightPrice)) && (
                  <FormRow layout="half-half">
                    <FieldLabel label="Price per gram">
                      <FieldNote
                        appearance="error"
                        note={formik.submitCount > 0 && formik.errors.weightPrice}
                      >
                        <NumberInputField
                          allowDecimals
                          name="weightPrice"
                          mixedLabel="Mixed"
                          mixed={isMixedValue(formik.values.weightPrice)}
                          value={
                            isMixedValue(formik.values.weightPrice)
                              ? (initialValues.weightPrice as string)
                              : (formik.values.weightPrice as string)
                          }
                          suffix={formatCurrency(provider.currency)}
                          appearance={hasError('weightPrice') ? 'error' : undefined}
                          onChange={(value, name) => formik.setFieldValue(name as string, value)}
                          onBlur={formik.handleBlur}
                          action={
                            isMixedValue(formik.values.weightPrice)
                              ? renderClearMixedValueButton('weightPrice')
                              : undefined
                          }
                        />
                      </FieldNote>
                    </FieldLabel>
                    {renderDensityField()}
                  </FormRow>
                )}
                {!isMixedValue(formik.values.weightPrice) &&
                  formik.values.weightPricingType === WeightPricingType.STAGGERED && (
                    <>
                      {renderStaggeredPricing(
                        formik.values.weightPrice as StaggeredWeightPrice[],
                        'weightPrice',
                        'weight',
                        'kg',
                        'Weight threshold',
                        'Price per gram',
                        nextIndex => {
                          formik.setFieldValue('weightPrice', [
                            ...(formik.values.weightPrice as StaggeredWeightPrice[]),
                            getInitialStaggeredWeightPrice(nextIndex),
                          ])
                        },
                        index => {
                          formik.setFieldValue(
                            'weightPrice',
                            (formik.values.weightPrice as StaggeredWeightPrice[]).filter(
                              (_, i) => i !== index
                            )
                          )
                        }
                      )}
                      <FormRow layout="half-half">
                        {renderDensityField()}
                        <div></div>
                      </FormRow>
                    </>
                  )}
              </>
            )}
          </MixedValueFormSection>
        </Stack>
        {formik.values.formula === PriceFormula.CNC_MACHINING && (
          <Stack space="90">
            <Headline label="Machining parameters" className="u-no-margin-bottom" />
            <Stack space="70">
              <FormRow layout="half-half">
                <FieldLabel label="Axis Tier">
                  <SelectField
                    name="cncAxisTier"
                    value={getCncAxisTierOption(formik.values.cncAxisTier)}
                    menu={<SelectMenu values={cncAxisTierOptions} />}
                    onChange={cncAxisTierOption => {
                      formik.setFieldValue('cncAxisTier', cncAxisTierOption.value)
                    }}
                    onBlur={formik.handleBlur}
                    mixedLabel="Mixed"
                    mixed={isMixedValue(formik.values.cncAxisTier)}
                  />
                  <TooltipHandler
                    placement={PopoverAnchorPlacement.bottomCenter}
                    tooltip={
                      <Tooltip>
                        Higher axis tiers (4-axis, 5-axis) can fulfill lower tier requirements. If
                        an exact tier match exists, it will be preferred over higher tiers. Choose
                        &quot;All axis&quot; to allow any axis requirement.
                      </Tooltip>
                    }
                  >
                    <Link appearance="noUnderline">How does axis tier matching work?</Link>
                  </TooltipHandler>
                </FieldLabel>
                <FieldLabel label="Cost per hour">
                  <FieldNote
                    appearance="error"
                    note={formik.submitCount > 0 && formik.errors.machiningCostPerHour}
                  >
                    <NumberInputField
                      allowDecimals
                      name="machiningCostPerHour"
                      mixedLabel="Mixed"
                      mixed={isMixedValue(formik.values.machiningCostPerHour)}
                      value={
                        isMixedValue(formik.values.machiningCostPerHour)
                          ? initialValues.machiningCostPerHour
                          : formik.values.machiningCostPerHour
                      }
                      suffix={`${formatCurrency(provider.currency)}/h`}
                      appearance={hasError('machiningCostPerHour') ? 'error' : undefined}
                      onChange={(value, name) => formik.setFieldValue(name as string, value)}
                      onBlur={formik.handleBlur}
                      action={
                        isMixedValue(formik.values.machiningCostPerHour)
                          ? renderClearMixedValueButton('machiningCostPerHour')
                          : undefined
                      }
                    />
                  </FieldNote>
                </FieldLabel>
              </FormRow>
            </Stack>
            <Stack space="70">
              <Headline label="Material removal rate" size="s" className="u-no-margin-bottom" />
              <FormRow layout="half-half">
                <FieldLabel label="Rough machining">
                  <FieldNote
                    appearance="error"
                    note={formik.submitCount > 0 && formik.errors.machiningMaterialRemovalRate}
                  >
                    <NumberInputField
                      allowDecimals
                      name="machiningMaterialRemovalRate"
                      mixedLabel="Mixed"
                      mixed={isMixedValue(formik.values.machiningMaterialRemovalRate)}
                      value={
                        isMixedValue(formik.values.machiningMaterialRemovalRate)
                          ? initialValues.machiningMaterialRemovalRate
                          : formik.values.machiningMaterialRemovalRate
                      }
                      suffix="min/cm³"
                      appearance={hasError('machiningMaterialRemovalRate') ? 'error' : undefined}
                      onChange={(value, name) => formik.setFieldValue(name as string, value)}
                      onBlur={formik.handleBlur}
                      action={
                        isMixedValue(formik.values.machiningMaterialRemovalRate)
                          ? renderClearMixedValueButton('machiningMaterialRemovalRate')
                          : undefined
                      }
                    />
                  </FieldNote>
                </FieldLabel>
                <FieldLabel label="Finishing">
                  <FieldNote
                    appearance="error"
                    note={formik.submitCount > 0 && formik.errors.finishingMaterialRemovalRate}
                  >
                    <NumberInputField
                      allowDecimals
                      name="finishingMaterialRemovalRate"
                      mixedLabel="Mixed"
                      mixed={isMixedValue(formik.values.finishingMaterialRemovalRate)}
                      value={
                        isMixedValue(formik.values.finishingMaterialRemovalRate)
                          ? initialValues.finishingMaterialRemovalRate
                          : formik.values.finishingMaterialRemovalRate
                      }
                      suffix="min/cm³"
                      appearance={hasError('finishingMaterialRemovalRate') ? 'error' : undefined}
                      onChange={(value, name) => formik.setFieldValue(name as string, value)}
                      onBlur={formik.handleBlur}
                      action={
                        isMixedValue(formik.values.finishingMaterialRemovalRate)
                          ? renderClearMixedValueButton('finishingMaterialRemovalRate')
                          : undefined
                      }
                    />
                  </FieldNote>
                </FieldLabel>
              </FormRow>
            </Stack>
            <Stack space="70">
              <Headline label="Setup costs" size="s" className="u-no-margin-bottom" />
              <FormRow layout="half-half">
                <FieldLabel label="Machine refixture time per setup" addendum="optional">
                  <FieldNote
                    appearance="error"
                    note={formik.submitCount > 0 && formik.errors.machineRefixtureTimePerSetup}
                  >
                    <NumberInputField
                      allowDecimals
                      name="machineRefixtureTimePerSetup"
                      mixedLabel="Mixed"
                      mixed={isMixedValue(formik.values.machineRefixtureTimePerSetup)}
                      value={
                        isMixedValue(formik.values.machineRefixtureTimePerSetup)
                          ? initialValues.machineRefixtureTimePerSetup
                          : formik.values.machineRefixtureTimePerSetup
                      }
                      suffix="minutes"
                      appearance={hasError('machineRefixtureTimePerSetup') ? 'error' : undefined}
                      onChange={(value, name) => formik.setFieldValue(name as string, value)}
                      onBlur={formik.handleBlur}
                      action={
                        isMixedValue(formik.values.machineRefixtureTimePerSetup)
                          ? renderClearMixedValueButton('machineRefixtureTimePerSetup')
                          : undefined
                      }
                    />
                  </FieldNote>
                </FieldLabel>
                <FieldLabel label="CAM programming fee per setup" addendum="optional">
                  <FieldNote
                    appearance="error"
                    note={formik.submitCount > 0 && formik.errors.camProgrammingFeePerSetup}
                  >
                    <NumberInputField
                      allowDecimals
                      name="camProgrammingFeePerSetup"
                      mixedLabel="Mixed"
                      mixed={isMixedValue(formik.values.camProgrammingFeePerSetup)}
                      value={
                        isMixedValue(formik.values.camProgrammingFeePerSetup)
                          ? initialValues.camProgrammingFeePerSetup
                          : formik.values.camProgrammingFeePerSetup
                      }
                      suffix={formatCurrency(provider.currency)}
                      appearance={hasError('camProgrammingFeePerSetup') ? 'error' : undefined}
                      onChange={(value, name) => formik.setFieldValue(name as string, value)}
                      onBlur={formik.handleBlur}
                      action={
                        isMixedValue(formik.values.camProgrammingFeePerSetup)
                          ? renderClearMixedValueButton('camProgrammingFeePerSetup')
                          : undefined
                      }
                    />
                  </FieldNote>
                </FieldLabel>
              </FormRow>
            </Stack>
          </Stack>
        )}
        <Stack space="90">
          <Headline label="Additional parameters" className="u-no-margin-bottom" />
          <Stack space="70">
            <Headline size="s" label="Model pricing" className="u-no-margin-bottom" />
            <FormRow layout="half-half">
              <FieldLabel label="Bounding box price" addendum="optional">
                <FieldNote
                  appearance="error"
                  note={formik.submitCount > 0 && formik.errors.boundingBoxPrice}
                >
                  <NumberInputField
                    allowDecimals
                    name="boundingBoxPrice"
                    mixedLabel="Mixed"
                    mixed={isMixedValue(formik.values.boundingBoxPrice)}
                    value={
                      isMixedValue(formik.values.boundingBoxPrice)
                        ? initialValues.boundingBoxPrice
                        : formik.values.boundingBoxPrice
                    }
                    suffix={formatCurrency(provider.currency)}
                    appearance={hasError('boundingBoxPrice') ? 'error' : undefined}
                    onChange={(value, name) => formik.setFieldValue(name as string, value)}
                    onBlur={formik.handleBlur}
                    action={
                      isMixedValue(formik.values.boundingBoxPrice)
                        ? renderClearMixedValueButton('boundingBoxPrice')
                        : undefined
                    }
                  />
                </FieldNote>
              </FieldLabel>
              <FieldLabel label="Surface price" addendum="optional">
                <FieldNote
                  appearance="error"
                  note={formik.submitCount > 0 && formik.errors.surfacePrice}
                >
                  <NumberInputField
                    allowDecimals
                    name="surfacePrice"
                    mixedLabel="Mixed"
                    mixed={isMixedValue(formik.values.surfacePrice)}
                    value={
                      isMixedValue(formik.values.surfacePrice)
                        ? initialValues.surfacePrice
                        : formik.values.surfacePrice
                    }
                    suffix={formatCurrency(provider.currency)}
                    appearance={hasError('surfacePrice') ? 'error' : undefined}
                    onChange={(value, name) => formik.setFieldValue(name as string, value)}
                    onBlur={formik.handleBlur}
                    action={
                      isMixedValue(formik.values.surfacePrice)
                        ? renderClearMixedValueButton('surfacePrice')
                        : undefined
                    }
                  />
                </FieldNote>
              </FieldLabel>
            </FormRow>
          </Stack>
          <Stack space="70">
            <Headline size="s" label="Base price" className="u-no-margin-bottom" />
            <FormRow layout="half-half">
              <FieldLabel label="Per piece" addendum="optional">
                <FieldNote
                  appearance="error"
                  note={formik.submitCount > 0 && formik.errors.baseUnitPrice}
                >
                  <NumberInputField
                    allowDecimals
                    name="baseUnitPrice"
                    mixedLabel="Mixed"
                    mixed={isMixedValue(formik.values.baseUnitPrice)}
                    value={
                      isMixedValue(formik.values.baseUnitPrice)
                        ? initialValues.baseUnitPrice
                        : formik.values.baseUnitPrice
                    }
                    suffix={formatCurrency(provider.currency)}
                    appearance={hasError('baseUnitPrice') ? 'error' : undefined}
                    onChange={value => formik.setFieldValue('baseUnitPrice', value)}
                    onBlur={formik.handleBlur}
                    action={
                      isMixedValue(formik.values.baseUnitPrice)
                        ? renderClearMixedValueButton('baseUnitPrice')
                        : undefined
                    }
                  />
                </FieldNote>
              </FieldLabel>
              <FieldLabel label="Per file" addendum="optional">
                <FieldNote
                  appearance="error"
                  note={formik.submitCount > 0 && formik.errors.basePrice}
                >
                  <NumberInputField
                    allowDecimals
                    name="basePrice"
                    mixedLabel="Mixed"
                    mixed={isMixedValue(formik.values.basePrice)}
                    value={
                      isMixedValue(formik.values.basePrice)
                        ? initialValues.basePrice
                        : formik.values.basePrice
                    }
                    suffix={formatCurrency(provider.currency)}
                    appearance={hasError('basePrice') ? 'error' : undefined}
                    onChange={(value, name) => formik.setFieldValue(name as string, value)}
                    onBlur={formik.handleBlur}
                    action={
                      isMixedValue(formik.values.basePrice)
                        ? renderClearMixedValueButton('basePrice')
                        : undefined
                    }
                  />
                </FieldNote>
              </FieldLabel>
            </FormRow>
          </Stack>
          <Stack space="70">
            <Headline size="s" label="Minimum price" className="u-no-margin-bottom" />
            <FormRow layout="half-half">
              <FieldLabel label="Per piece" addendum="optional">
                <FieldNote
                  appearance="error"
                  note={formik.submitCount > 0 && formik.errors.minimumUnitPrice}
                >
                  <NumberInputField
                    allowDecimals
                    name="minimumUnitPrice"
                    mixedLabel="Mixed"
                    mixed={isMixedValue(formik.values.minimumUnitPrice)}
                    value={
                      isMixedValue(formik.values.minimumUnitPrice)
                        ? initialValues.minimumUnitPrice
                        : formik.values.minimumUnitPrice
                    }
                    suffix={formatCurrency(provider.currency)}
                    appearance={hasError('minimumUnitPrice') ? 'error' : undefined}
                    onChange={(value, name) => formik.setFieldValue(name as string, value)}
                    onBlur={formik.handleBlur}
                    action={
                      isMixedValue(formik.values.minimumUnitPrice)
                        ? renderClearMixedValueButton('minimumUnitPrice')
                        : undefined
                    }
                  />
                </FieldNote>
              </FieldLabel>
              <FieldLabel label="Per file" addendum="optional">
                <FieldNote
                  appearance="error"
                  note={formik.submitCount > 0 && formik.errors.minimumPrice}
                >
                  <NumberInputField
                    allowDecimals
                    name="minimumPrice"
                    mixedLabel="Mixed"
                    mixed={isMixedValue(formik.values.minimumPrice)}
                    value={
                      isMixedValue(formik.values.minimumPrice)
                        ? initialValues.minimumPrice
                        : formik.values.minimumPrice
                    }
                    suffix={formatCurrency(provider.currency)}
                    appearance={hasError('minimumPrice') ? 'error' : undefined}
                    onChange={(value, name) => formik.setFieldValue(name as string, value)}
                    onBlur={formik.handleBlur}
                    action={
                      isMixedValue(formik.values.minimumPrice)
                        ? renderClearMixedValueButton('minimumPrice')
                        : undefined
                    }
                  />
                </FieldNote>
              </FieldLabel>
            </FormRow>
          </Stack>
        </Stack>
        <Stack space="90">
          <Headline size="s" label="Quantity Discount" className="u-no-margin-bottom" />
          <Paragraph size="xs" minor className="u-no-margin-bottom">
            The quantity discount formula allows you to grant a gradually increasing discount with
            increasing quantity. The discount factor is calculated as quantity ^ discount exponent.
            <br />
            The discount cap allows you to specify a limit for the discounted price as a maximum
            percentage of the original price.
            <br />
            <strong>
              If you want to set a maximum discount of 10%, the cap should be configured as 90%.
            </strong>
            <br />
            Use the example calculator with different quantities to get a feel for the behavior of
            these pricing parameters.
          </Paragraph>
          <FormRow layout="half-half">
            <FieldLabel label="Exponent" addendum="optional">
              <FieldNote
                appearance="error"
                note={formik.submitCount > 0 && formik.errors.quantityDiscount}
              >
                <NumberInputField
                  allowDecimals
                  name="quantityDiscount"
                  mixedLabel="Mixed"
                  mixed={isMixedValue(formik.values.quantityDiscount)}
                  value={
                    isMixedValue(formik.values.quantityDiscount)
                      ? initialValues.quantityDiscount
                      : formik.values.quantityDiscount
                  }
                  appearance={hasError('quantityDiscount') ? 'error' : undefined}
                  onChange={(value, name) => formik.setFieldValue(name as string, value)}
                  onBlur={formik.handleBlur}
                  action={
                    isMixedValue(formik.values.quantityDiscount)
                      ? renderClearMixedValueButton('quantityDiscount')
                      : undefined
                  }
                />
              </FieldNote>
            </FieldLabel>
            <FieldLabel label="Cap" addendum="optional">
              <FieldNote
                appearance="error"
                note={formik.submitCount > 0 && formik.errors.quantityDiscountCap}
              >
                <NumberInputField
                  allowDecimals
                  suffix="%"
                  name="quantityDiscountCap"
                  mixedLabel="Mixed"
                  mixed={isMixedValue(formik.values.quantityDiscountCap)}
                  value={
                    isMixedValue(formik.values.quantityDiscountCap)
                      ? initialValues.quantityDiscountCap
                      : formik.values.quantityDiscountCap
                  }
                  appearance={hasError('quantityDiscountCap') ? 'error' : undefined}
                  onChange={(value, name) => formik.setFieldValue(name as string, value)}
                  onBlur={formik.handleBlur}
                  action={
                    isMixedValue(formik.values.quantityDiscountCap)
                      ? renderClearMixedValueButton('quantityDiscountCap')
                      : undefined
                  }
                />
              </FieldNote>
            </FieldLabel>
          </FormRow>
        </Stack>
        {formik.values.formula !== PriceFormula.CNC_MACHINING && renderSlicerUsage()}
      </Stack>
    </form>
  )
}

export default PricingsForm
